import { useEffect, useState, useCallback } from "react";
import { api, PAYOR_KIND_LABELS, ADVISOR_ROLE_LABELS } from "@/lib/api";
import { useHousehold } from "@/context/HouseholdContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, PencilSimple, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";

const payorEmpty = { name: "", kind: "property_manager", email: "", phone: "", notes: "" };
const advEmpty = { name: "", role: "cpa", firm: "", handles: "", email: "", phone: "", notes: "" };

function CrudList({ kind }) {
  const { hid, isOperator } = useHousehold();
  const isPayor = kind === "payor";
  const path = isPayor ? "payors" : "advisors";
  const labels = isPayor ? PAYOR_KIND_LABELS : ADVISOR_ROLE_LABELS;
  const empty = isPayor ? payorEmpty : advEmpty;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => { if (hid) api.get(`/${path}?household_id=${hid}`).then((r) => setItems(r.data)).catch(() => {}); }, [hid, path]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editId) await api.put(`/${path}/${editId}?household_id=${hid}`, form);
      else await api.post(`/${path}?household_id=${hid}`, form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error("Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/${path}/${id}?household_id=${hid}`); load(); };

  return (
    <div className="space-y-4 mt-4">
      {isOperator && <Button data-testid={`add-${kind}-button`} onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="rounded-sm font-semibold"><Plus size={15} className="mr-1" /> Add {isPayor ? "Payor" : "Advisor"}</Button>}
      {items.length === 0 ? <p className="text-sm text-muted-foreground">{isPayor ? "No payors yet — add who sends money in (property managers, mineral operators, fund admins)." : "No advisors yet — add your CPA, attorney, insurance agent."}</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`${kind}-card-${it.id}`} className="bg-card border border-border rounded-sm p-4 group">
              <div className="flex items-start justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-secondary px-2 py-1 rounded-sm">{labels[isPayor ? it.kind : it.role] || "—"}</span>
                {isOperator && <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity"><button data-testid={`edit-${kind}-${it.id}`} onClick={() => { setForm({ ...empty, ...it }); setEditId(it.id); setOpen(true); }} className="text-muted-foreground hover:text-foreground"><PencilSimple size={14} /></button><button data-testid={`delete-${kind}-${it.id}`} onClick={() => remove(it.id)} className="text-muted-foreground hover:text-destructive"><Trash size={14} /></button></div>}
              </div>
              <h3 className="font-semibold mt-2">{it.name}</h3>
              {!isPayor && it.firm && <p className="text-xs text-muted-foreground">{it.firm}</p>}
              {!isPayor && it.handles && <p className="text-sm text-muted-foreground mt-1">{it.handles}</p>}
              <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                {it.email && <p className="flex items-center gap-1.5 truncate"><EnvelopeSimple size={12} /> {it.email}</p>}
                {it.phone && <p className="flex items-center gap-1.5"><Phone size={12} /> {it.phone}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black">{editId ? "Edit" : "New"} {isPayor ? "Payor" : "Advisor"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider font-bold">Name</Label><Input data-testid={`${kind}-name-input`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-wider font-bold">{isPayor ? "Type" : "Role"}</Label>
              <Select value={isPayor ? form.kind : form.role} onValueChange={(v) => setForm({ ...form, [isPayor ? "kind" : "role"]: v })}><SelectTrigger data-testid={`${kind}-type-select`} className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(labels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            {!isPayor && <div><Label className="text-xs uppercase tracking-wider font-bold">Firm</Label><Input value={form.firm} onChange={(e) => setForm({ ...form, firm: e.target.value })} className="rounded-sm mt-1" /></div>}
            {!isPayor && <div><Label className="text-xs uppercase tracking-wider font-bold">What they handle</Label><Input value={form.handles} onChange={(e) => setForm({ ...form, handles: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. Federal taxes, estate planning" /></div>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider font-bold">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm mt-1" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-bold">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm mt-1" /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid={`save-${kind}-button`} onClick={save} className="rounded-sm font-semibold w-full">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Contacts() {
  return (
    <div className="space-y-6 animate-fade-up">
      <div><p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Who's involved</p><h1 className="font-display text-4xl font-black tracking-tighter mt-1">Contacts</h1></div>
      <Tabs defaultValue="payors">
        <TabsList className="rounded-sm"><TabsTrigger data-testid="tab-payors" value="payors" className="rounded-sm">Payors</TabsTrigger><TabsTrigger data-testid="tab-advisors" value="advisors" className="rounded-sm">Advisors</TabsTrigger></TabsList>
        <TabsContent value="payors"><CrudList kind="payor" /></TabsContent>
        <TabsContent value="advisors"><CrudList kind="advisor" /></TabsContent>
      </Tabs>
    </div>
  );
}
